import React, { useState, useEffect } from 'react';
import { emailsService, userService } from '../services/api';
import TemplateEditor from '../components/TemplateEditor';
import EnvoiEmailTemplateModal from '../components/EnvoiEmailTemplateModal';
import '../styles/GestionEmails.css';
import logger from '../utils/logger';

const GestionEmails = () => {
  const [activeTab, setActiveTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [settings, setSettings] = useState([]);
  const [variablesDisponibles, setVariablesDisponibles] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // États pour l'édition de template
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [editingSettings, setEditingSettings] = useState(null);
  const [showEnvoiModal, setShowEnvoiModal] = useState(false);
  const [showEnvoiTemplateModal, setShowEnvoiTemplateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  // États pour l'onglet utilitaires
  const [resetPasswordEmail, setResetPasswordEmail] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchUser, setSearchUser] = useState('');

  // États pour les filtres
  const [logFilters, setLogFilters] = useState({
    statut: '',
    destinataire: '',
    template_id: ''
  });

  // États pour l'envoi d'email
  const [envoiEmail, setEnvoiEmail] = useState({
    destinataire_email: '',
    sujet: '',
    corps: '',
    inscription_id: null
  });

  useEffect(() => {
    loadData();
    loadVariablesDisponibles();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 'templates') {
        const response = await emailsService.getTemplates();
        setTemplates(response.data.results || response.data);
      } else if (activeTab === 'logs') {
        const response = await emailsService.getLogs(logFilters);
        setLogs(response.data.results || response.data);
      } else if (activeTab === 'settings') {
        const response = await emailsService.getSettings();
        setSettings(response.data.results || response.data);
      } else if (activeTab === 'utilities') {
        const response = await userService.getAll();
        const users = response.data.results || response.data;
        const parentsOnly = users.filter(u => u.role === 'PARENT');
        setAllUsers(parentsOnly);
        setFilteredUsers(parentsOnly);
      }
    } catch (err) {
      setError('Erreur lors du chargement des données');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadVariablesDisponibles = async () => {
    try {
      const response = await emailsService.getVariablesDisponibles();
      setVariablesDisponibles(response.data);
    } catch (err) {
      logger.error('Erreur lors du chargement des variables:', err);
    }
  };

  const handleSaveTemplate = async (templateData) => {
    setLoading(true);
    setError(null);
    try {
      if (templateData.id) {
        await emailsService.updateTemplate(templateData.id, templateData);
        setSuccess('Template mis à jour avec succès');
      } else {
        await emailsService.createTemplate(templateData);
        setSuccess('Template créé avec succès');
      }
      setShowTemplateModal(false);
      setEditingTemplate(null);
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Erreur lors de la sauvegarde du template');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce template ?')) {
      return;
    }
    setLoading(true);
    try {
      await emailsService.deleteTemplate(id);
      setSuccess('Template supprimé avec succès');
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Erreur lors de la suppression du template');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDupliquerTemplate = async (id) => {
    setLoading(true);
    try {
      await emailsService.dupliquerTemplate(id);
      setSuccess('Template dupliqué avec succès');
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Erreur lors de la duplication du template');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewTemplate = async (template, inscriptionId = null) => {
    setLoading(true);
    try {
      const response = await emailsService.previewTemplate(template.id, inscriptionId);
      setPreviewData({
        sujet: response.data.sujet,
        corps: response.data.corps,
        variables_utilisees: response.data.variables_utilisees,
        template_nom: template.nom,
        inscription_id: inscriptionId
      });
      setShowPreviewModal(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la prévisualisation');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEnvoiEmail = async () => {
    setLoading(true);
    setError(null);
    try {
      // Préparer les données en convertissant inscription_id vide en null
      const emailData = {
        ...envoiEmail,
        inscription_id: envoiEmail.inscription_id || null
      };
      await emailsService.envoyerEmail(emailData);
      setSuccess('Email envoyé avec succès');
      setShowEnvoiModal(false);
      setEnvoiEmail({
        destinataire_email: '',
        sujet: '',
        corps: '',
        inscription_id: null
      });
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Erreur lors de l\'envoi de l\'email');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (settingsData) => {
    setLoading(true);
    setError(null);
    try {
      if (settingsData.id) {
        await emailsService.updateSettings(settingsData.id, settingsData);
        setSuccess('Configuration mise à jour avec succès');
      } else {
        await emailsService.createSettings(settingsData);
        setSuccess('Configuration créée avec succès');
      }
      setShowSettingsModal(false);
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Erreur lors de la sauvegarde de la configuration');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTesterConfiguration = async (id) => {
    setLoading(true);
    setError(null);
    try {
      logger.log('[GestionEmails] Test de la configuration', id);
      const response = await emailsService.testerConfiguration(id);
      logger.log('[GestionEmails] Réponse reçue:', response.data);
      setSuccess(response.data.message);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      logger.error('[GestionEmails] Erreur lors du test:', err);
      const errorMessage = err.response?.data?.error
        || err.message
        || 'Erreur lors du test de configuration (timeout ou connexion impossible)';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleActiverConfiguration = async (id) => {
    setLoading(true);
    try {
      await emailsService.activerConfiguration(id);
      setSuccess('Configuration activée avec succès');
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Erreur lors de l\'activation de la configuration');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSettings = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette configuration SMTP ?')) {
      return;
    }
    setLoading(true);
    try {
      await emailsService.deleteSettings(id);
      setSuccess('Configuration supprimée avec succès');
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression de la configuration');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour envoyer un email de reset password
  const handleSendResetPasswordEmail = async (userId, userEmail) => {
    if (!window.confirm(`Voulez-vous vraiment envoyer un email de réinitialisation de mot de passe à ${userEmail} ?`)) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await userService.sendPasswordResetEmail(userId);
      setSuccess(response.data.message || 'Email de réinitialisation envoyé avec succès');
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Erreur lors de l\'envoi de l\'email de réinitialisation';
      setError(errorMsg);
      logger.error(err);
      setTimeout(() => setError(null), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour filtrer les utilisateurs
  useEffect(() => {
    if (searchUser.trim() === '') {
      setFilteredUsers(allUsers);
    } else {
      const search = searchUser.toLowerCase();
      const filtered = allUsers.filter(user =>
        user.email.toLowerCase().includes(search) ||
        (user.first_name && user.first_name.toLowerCase().includes(search)) ||
        (user.last_name && user.last_name.toLowerCase().includes(search))
      );
      setFilteredUsers(filtered);
    }
  }, [searchUser, allUsers]);

  const renderTemplatesTab = () => (
    <div className="templates-tab">
      <div className="tab-header">
        <h2>Templates d'emails</h2>
        <button
          className="btn-primary"
          onClick={() => {
            setEditingTemplate({
              nom: '',
              description: '',
              sujet: '',
              corps: '',
              actif: true,
              envoyer_automatiquement: false,
              evenement_declencheur: null
            });
            setShowTemplateModal(true);
          }}
        >
          + Nouveau template
        </button>
      </div>

      {templates.length === 0 ? (
        <div className="empty-state">
          <p>Aucun template configuré</p>
        </div>
      ) : (
        <div className="templates-grid">
          {templates.map((template) => (
            <div key={template.id} className="template-card">
              <div className="template-card-header">
                <h3>{template.nom}</h3>
                <div className="template-badges">
                  {template.actif && <span className="badge badge-success">Actif</span>}
                  {template.envoyer_automatiquement && (
                    <span className="badge badge-info">Auto</span>
                  )}
                </div>
              </div>
              <div className="template-card-body">
                {template.evenement_declencheur_display && (
                  <p className="template-event">
                    <strong>Événement:</strong> {template.evenement_declencheur_display}
                  </p>
                )}
                {template.description && (
                  <p className="template-description">{template.description}</p>
                )}
                <p className="template-sujet">
                  <strong>Sujet:</strong> {template.sujet}
                </p>
                {(template.cc_emails || template.bcc_emails) && (
                  <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                    {template.cc_emails && (
                      <div style={{ marginBottom: '5px' }}>
                        <strong>CC:</strong> {template.cc_emails}
                      </div>
                    )}
                    {template.bcc_emails && (
                      <div>
                        <strong>BCC:</strong> {template.bcc_emails}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="template-card-footer">
                <button
                  className="btn-primary btn-sm"
                  onClick={() => {
                    setSelectedTemplate(template);
                    setShowEnvoiTemplateModal(true);
                  }}
                  title="Envoyer cet email à une inscription"
                >
                  Envoyer
                </button>
                <button
                  className="btn-secondary btn-sm"
                  onClick={() => handlePreviewTemplate(template)}
                >
                  Prévisualiser
                </button>
                <button
                  className="btn-secondary btn-sm"
                  onClick={() => {
                    setEditingTemplate(template);
                    setShowTemplateModal(true);
                  }}
                >
                  Modifier
                </button>
                <button
                  className="btn-secondary btn-sm"
                  onClick={() => handleDupliquerTemplate(template.id)}
                >
                  Dupliquer
                </button>
                <button
                  className="btn-danger btn-sm"
                  onClick={() => handleDeleteTemplate(template.id)}
                >
                  Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderLogsTab = () => (
    <div className="logs-tab">
      <div className="tab-header">
        <h2>Historique des emails</h2>
        <div className="log-filters">
          <select
            value={logFilters.statut}
            onChange={(e) => {
              setLogFilters({ ...logFilters, statut: e.target.value });
            }}
          >
            <option value="">Tous les statuts</option>
            <option value="ENVOYE">Envoyé</option>
            <option value="ERREUR">Erreur</option>
            <option value="EN_ATTENTE">En attente</option>
          </select>
          <input
            type="text"
            placeholder="Rechercher par destinataire..."
            value={logFilters.destinataire}
            onChange={(e) => {
              setLogFilters({ ...logFilters, destinataire: e.target.value });
            }}
          />
          <button className="btn-secondary" onClick={loadData}>
            Filtrer
          </button>
        </div>
      </div>

      {logs.length === 0 ? (
        <div className="empty-state">
          <p>Aucun email envoyé</p>
        </div>
      ) : (
        <div className="logs-table-container">
          <table className="logs-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Inscription</th>
                <th>Destinataire</th>
                <th>Sujet</th>
                <th>Template</th>
                <th>Statut</th>
                <th>Envoyé par</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td>{new Date(log.envoye_le).toLocaleString('fr-FR')}</td>
                  <td>
                    {log.inscription ? (
                      <span className="badge badge-info">Inscription</span>
                    ) : (
                      <span className="badge badge-warning">Système</span>
                    )}
                  </td>
                  <td>
                    {log.inscription ? (
                      <div>
                        {log.inscription_numero && <div style={{fontWeight: 'bold', fontSize: '0.85em'}}>{log.inscription_numero}</div>}
                        {log.inscription_enfant && <div style={{fontSize: '0.9em'}}>{log.inscription_enfant}</div>}
                        {log.inscription_sejour && <div style={{fontSize: '0.85em', color: '#666'}}>{log.inscription_sejour}</div>}
                      </div>
                    ) : '-'}
                  </td>
                  <td>
                    {log.destinataire_nom && <div style={{fontWeight: '500'}}>{log.destinataire_nom}</div>}
                    <div className="email-address" style={{fontSize: '0.85em', color: '#666'}}>{log.destinataire_email}</div>
                  </td>
                  <td style={{maxWidth: '250px'}}>{log.sujet}</td>
                  <td>{log.template_nom || <span style={{color: '#999'}}>Manuel</span>}</td>
                  <td>
                    <span className={`badge badge-${log.statut === 'ENVOYE' ? 'success' : log.statut === 'ERREUR' ? 'danger' : 'warning'}`}>
                      {log.statut_display}
                    </span>
                    {log.statut === 'ERREUR' && log.erreur && (
                      <div style={{fontSize: '0.75em', color: '#dc3545', marginTop: '3px'}} title={log.erreur}>
                        {log.erreur.substring(0, 50)}{log.erreur.length > 50 ? '...' : ''}
                      </div>
                    )}
                  </td>
                  <td>{log.envoye_par_nom || '-'}</td>
                  <td>
                    <button
                      className="btn-secondary btn-sm"
                      onClick={() => {
                        setPreviewData({
                          sujet: log.sujet,
                          corps: log.corps
                        });
                        setShowPreviewModal(true);
                      }}
                    >
                      Voir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderSettingsTab = () => (
    <div className="settings-tab">
      <div className="tab-header">
        <h2>Configuration SMTP</h2>
        <button
          className="btn-primary"
          onClick={() => {
            setEditingSettings(null);
            setShowSettingsModal(true);
          }}
        >
          + Nouvelle configuration
        </button>
      </div>

      {settings.length === 0 ? (
        <div className="empty-state">
          <p>Aucune configuration SMTP</p>
          <button className="btn-primary" onClick={() => {
            setEditingSettings(null);
            setShowSettingsModal(true);
          }}>
            Créer une configuration
          </button>
        </div>
      ) : (
        <div className="settings-grid">
          {settings.map((setting) => (
            <div key={setting.id} className="settings-card">
              <div className="settings-card-header">
                <h3>{setting.from_name}</h3>
                <div style={{display: 'flex', gap: '5px'}}>
                  <span className={`badge ${setting.type_config === 'INSCRIPTIONS' ? 'badge-info' : 'badge-warning'}`}>
                    {setting.type_config_display || setting.type_config}
                  </span>
                  {setting.actif && <span className="badge badge-success">Active</span>}
                </div>
              </div>
              <div className="settings-card-body">
                {setting.description && <p><strong>Description:</strong> {setting.description}</p>}
                <p><strong>Email expéditeur:</strong> {setting.from_email}</p>
                <p><strong>Serveur SMTP:</strong> {setting.smtp_host}:{setting.smtp_port}</p>
                <p><strong>Utilisateur:</strong> {setting.smtp_username}</p>
                <p>
                  <strong>Sécurité:</strong>{' '}
                  {setting.smtp_use_tls ? 'TLS' : setting.smtp_use_ssl ? 'SSL' : 'Aucune'}
                </p>
              </div>
              <div className="settings-card-footer">
                <button
                  className="btn-secondary btn-sm"
                  onClick={() => handleTesterConfiguration(setting.id)}
                >
                  Tester
                </button>
                {!setting.actif && (
                  <button
                    className="btn-primary btn-sm"
                    onClick={() => handleActiverConfiguration(setting.id)}
                  >
                    Activer
                  </button>
                )}
                <button
                  className="btn-secondary btn-sm"
                  onClick={() => {
                    setEditingSettings(setting);
                    setShowSettingsModal(true);
                  }}
                >
                  Modifier
                </button>
                {settings.length > 1 && (
                  <button
                    className="btn-danger btn-sm"
                    onClick={() => handleDeleteSettings(setting.id)}
                  >
                    Supprimer
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const handleEnvoiTemplateSuccess = () => {
    setSuccess('Email envoyé avec succès');
    loadData();
    setTimeout(() => setSuccess(null), 3000);
  };

  const renderUtilitiesTab = () => (
    <div className="utilities-tab">
      <div className="tab-header">
        <h2>Utilitaires Email</h2>
      </div>

      <div style={{ marginBottom: '30px', backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px' }}>
        <h3 style={{ marginTop: 0, marginBottom: '15px' }}>Envoyer un email de réinitialisation de mot de passe</h3>
        <p style={{ color: '#666', marginBottom: '20px' }}>
          Permet d'envoyer manuellement un email de réinitialisation de mot de passe à un parent.
          Le lien sera valide pendant 24 heures.
        </p>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Rechercher un parent (nom, prénom ou email)
          </label>
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchUser}
            onChange={(e) => setSearchUser(e.target.value)}
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          />
        </div>

        {filteredUsers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
            {searchUser ? 'Aucun parent trouvé' : 'Aucun parent enregistré'}
          </div>
        ) : (
          <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#f1f1f1', position: 'sticky', top: 0 }}>
                <tr>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Nom</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Prénom</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Email</th>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px' }}>{user.last_name || '-'}</td>
                    <td style={{ padding: '12px' }}>{user.first_name || '-'}</td>
                    <td style={{ padding: '12px' }}>{user.email}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <button
                        className="btn-primary btn-sm"
                        onClick={() => handleSendResetPasswordEmail(user.id, user.email)}
                        disabled={loading}
                        style={{ fontSize: '13px' }}
                      >
                        Envoyer email reset
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ marginTop: '15px', padding: '12px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px' }}>
          <strong>Note importante :</strong>
          <ul style={{ marginBottom: 0, marginTop: '8px', paddingLeft: '20px' }}>
            <li>Cette fonctionnalité ne fonctionne que pour les comptes parents</li>
            <li>L'email contient un lien valide pendant 24 heures</li>
            <li>Un log sera créé dans l'onglet "Historique"</li>
          </ul>
        </div>
      </div>
    </div>
  );

  return (
    <div className="gestion-emails">
      <div className="page-header">
        <h1>Gestion des Emails</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn-primary" onClick={() => setShowEnvoiTemplateModal(true)}>
            📧 Envoyer avec template
          </button>
          <button className="btn-secondary" onClick={() => setShowEnvoiModal(true)}>
            Envoyer manuellement
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          {error}
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
          <button onClick={() => setSuccess(null)}>&times;</button>
        </div>
      )}

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'templates' ? 'active' : ''}`}
          onClick={() => setActiveTab('templates')}
        >
          Templates
        </button>
        <button
          className={`tab ${activeTab === 'logs' ? 'active' : ''}`}
          onClick={() => setActiveTab('logs')}
        >
          Historique
        </button>
        <button
          className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          Configuration SMTP
        </button>
        <button
          className={`tab ${activeTab === 'utilities' ? 'active' : ''}`}
          onClick={() => setActiveTab('utilities')}
        >
          Utilitaires
        </button>
      </div>

      <div className="tab-content">
        {loading ? (
          <div className="loading">Chargement...</div>
        ) : (
          <>
            {activeTab === 'templates' && renderTemplatesTab()}
            {activeTab === 'logs' && renderLogsTab()}
            {activeTab === 'settings' && renderSettingsTab()}
            {activeTab === 'utilities' && renderUtilitiesTab()}
          </>
        )}
      </div>

      {/* Modals */}
      {showTemplateModal && (
        <TemplateEditor
          template={editingTemplate}
          variables={variablesDisponibles}
          onSave={handleSaveTemplate}
          onClose={() => {
            setShowTemplateModal(false);
            setEditingTemplate(null);
          }}
        />
      )}

      {showPreviewModal && previewData && (
        <PreviewModal
          data={previewData}
          onClose={() => {
            setShowPreviewModal(false);
            setPreviewData(null);
          }}
        />
      )}

      {showEnvoiModal && (
        <EnvoiEmailModal
          data={envoiEmail}
          onChange={setEnvoiEmail}
          onSend={handleEnvoiEmail}
          onClose={() => setShowEnvoiModal(false)}
          variables={variablesDisponibles}
        />
      )}

      {showSettingsModal && (
        <SettingsModal
          settings={editingSettings}
          onSave={handleSaveSettings}
          onClose={() => {
            setShowSettingsModal(false);
            setEditingSettings(null);
          }}
        />
      )}

      {showEnvoiTemplateModal && (
        <EnvoiEmailTemplateModal
          onSend={handleEnvoiTemplateSuccess}
          onClose={() => {
            setShowEnvoiTemplateModal(false);
            setSelectedTemplate(null);
          }}
          initialTemplate={selectedTemplate}
        />
      )}
    </div>
  );
};

// Composants modaux
const PreviewModal = ({ data, onClose}) => (
  <div className="modal-overlay">
    <div className="modal-content large">
      <div className="modal-header">
        <h2>Prévisualisation de l'email</h2>
        <button className="close-btn" onClick={onClose}>&times;</button>
      </div>
      <div className="modal-body">
        {/* Informations contextuelles */}
        {data.template_nom && (
          <div style={{
            backgroundColor: '#e7f3ff',
            border: '1px solid #b3d9ff',
            borderRadius: '6px',
            padding: '12px 15px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            <div style={{ marginBottom: '5px' }}>
              <strong>Template :</strong> {data.template_nom}
            </div>
            {data.inscription_id ? (
              <div style={{ color: '#28a745' }}>
                ✓ Aperçu avec les données de l'inscription #{data.inscription_id}
              </div>
            ) : (
              <div style={{ color: '#666' }}>
                📋 Aperçu avec exemple de variables
              </div>
            )}
          </div>
        )}

        {/* Aperçu style client email */}
        <div style={{
          border: '1px solid #ddd',
          borderRadius: '8px',
          overflow: 'hidden',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          {/* En-tête de l'email */}
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '15px 20px',
            borderBottom: '1px solid #ddd'
          }}>
            <div style={{
              fontSize: '12px',
              color: '#666',
              marginBottom: '8px'
            }}>
              <strong>Sujet :</strong>
            </div>
            <div style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#333'
            }}>
              {data.sujet}
            </div>
          </div>

          {/* Corps de l'email avec rendu HTML */}
          <div style={{
            padding: '20px',
            backgroundColor: '#fff',
            minHeight: '200px',
            fontFamily: 'Arial, sans-serif',
            lineHeight: '1.6',
            color: '#333'
          }}>
            <div dangerouslySetInnerHTML={{ __html: data.corps }} />
          </div>
        </div>

        {/* Section variables (optionnelle, pour le debug) */}
        {data.variables_utilisees && Object.keys(data.variables_utilisees).length > 0 && (
          <details style={{ marginTop: '20px' }}>
            <summary style={{
              cursor: 'pointer',
              padding: '10px',
              backgroundColor: '#f8f9fa',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontWeight: '500',
              userSelect: 'none'
            }}>
              📋 Variables utilisées ({Object.keys(data.variables_utilisees).length}) - Données réelles insérées
            </summary>
            <div style={{
              marginTop: '10px',
              padding: '15px',
              backgroundColor: '#f8f9fa',
              border: '1px solid #ddd',
              borderRadius: '4px',
              maxHeight: '300px',
              overflowY: 'auto'
            }}>
              <table style={{
                width: '100%',
                fontSize: '13px',
                borderCollapse: 'collapse'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#e9ecef' }}>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Variable</th>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Valeur</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(data.variables_utilisees).map(([key, value]) => (
                    <tr key={key} style={{ borderBottom: '1px solid #e0e0e0' }}>
                      <td style={{ padding: '8px', fontFamily: 'monospace', color: '#0066cc' }}>
                        {'{' + key + '}'}
                      </td>
                      <td style={{ padding: '8px', wordBreak: 'break-word' }}>
                        {value || <em style={{ color: '#999' }}>(vide)</em>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}
      </div>
      <div className="modal-footer">
        <button className="btn-secondary" onClick={onClose}>Fermer</button>
      </div>
    </div>
  </div>
);

const EnvoiEmailModal = ({ data, onChange, onSend, onClose, variables }) => (
  <div className="modal-overlay">
    <div className="modal-content">
      <div className="modal-header">
        <h2>Envoyer un email</h2>
        <button className="close-btn" onClick={onClose}>&times;</button>
      </div>
      <div className="modal-body">
        <div className="form-group">
          <label>Email destinataire</label>
          <input
            type="email"
            value={data.destinataire_email}
            onChange={(e) => onChange({ ...data, destinataire_email: e.target.value })}
            required
          />
        </div>
        <div className="form-group">
          <label>ID Inscription (optionnel)</label>
          <input
            type="number"
            value={data.inscription_id || ''}
            onChange={(e) => onChange({ ...data, inscription_id: e.target.value ? parseInt(e.target.value) : null })}
          />
        </div>
        <div className="form-group">
          <label>Sujet</label>
          <input
            type="text"
            value={data.sujet}
            onChange={(e) => onChange({ ...data, sujet: e.target.value })}
            required
          />
        </div>
        <div className="form-group">
          <label>Corps du message (HTML)</label>
          <textarea
            rows="10"
            value={data.corps}
            onChange={(e) => onChange({ ...data, corps: e.target.value })}
            required
          />
        </div>
      </div>
      <div className="modal-footer">
        <button className="btn-secondary" onClick={onClose}>Annuler</button>
        <button className="btn-primary" onClick={onSend}>Envoyer</button>
      </div>
    </div>
  </div>
);

const SettingsModal = ({ settings, onSave, onClose }) => {
  const [formData, setFormData] = useState(settings || {
    type_config: 'INSCRIPTIONS',
    smtp_host: '',
    smtp_port: 587,
    smtp_use_tls: true,
    smtp_use_ssl: false,
    smtp_username: '',
    smtp_password: '',
    from_email: '',
    from_name: '',
    description: '',
    actif: true
  });

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>{settings ? 'Modifier' : 'Nouvelle'} Configuration SMTP</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Type de configuration</label>
            <select
              value={formData.type_config}
              onChange={(e) => setFormData({ ...formData, type_config: e.target.value })}
            >
              <option value="INSCRIPTIONS">Inscriptions / Séjours</option>
              <option value="ADMINISTRATION">Administration / Système</option>
            </select>
            <small style={{display: 'block', marginTop: '5px', color: '#666'}}>
              {formData.type_config === 'INSCRIPTIONS'
                ? 'Utilisé pour les emails liés aux inscriptions, séjours, paiements'
                : 'Utilisé pour les emails système (réinitialisation mot de passe, notifications admin)'}
            </small>
          </div>
          <div className="form-group">
            <label>Description (optionnel)</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Ex: Serveur principal, Backup, etc."
            />
          </div>
          <div className="form-group">
            <label>Serveur SMTP</label>
            <input
              type="text"
              value={formData.smtp_host}
              onChange={(e) => setFormData({ ...formData, smtp_host: e.target.value })}
              placeholder="smtp.gmail.com"
            />
          </div>
          <div className="form-group">
            <label>Port</label>
            <input
              type="number"
              value={formData.smtp_port}
              onChange={(e) => setFormData({ ...formData, smtp_port: parseInt(e.target.value) })}
            />
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.smtp_use_tls}
                onChange={(e) => setFormData({ ...formData, smtp_use_tls: e.target.checked })}
              />
              Utiliser TLS
            </label>
          </div>
          <div className="form-group">
            <label>Nom d'utilisateur</label>
            <input
              type="text"
              value={formData.smtp_username}
              onChange={(e) => setFormData({ ...formData, smtp_username: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Mot de passe</label>
            <input
              type="password"
              value={formData.smtp_password}
              onChange={(e) => setFormData({ ...formData, smtp_password: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Email expéditeur</label>
            <input
              type="email"
              value={formData.from_email}
              onChange={(e) => setFormData({ ...formData, from_email: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Nom de l'expéditeur</label>
            <input
              type="text"
              value={formData.from_name}
              onChange={(e) => setFormData({ ...formData, from_name: e.target.value })}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn-primary" onClick={() => onSave(formData)}>Sauvegarder</button>
        </div>
      </div>
    </div>
  );
};

export default GestionEmails;
